import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Building2, 
  Landmark, 
  Calendar, 
  Euro, 
  FileText, 
  User, 
  CreditCard,
  Search,
  Check,
  Link2,
  Loader2,
  Home,
  Percent
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

interface SuggestedMatch {
  rent_period_id: string;
  lease_id: string;
  tenant_name: string;
  property_address: string;
  period_yyyymm: string;
  due_date: string;
  amount_due: number;
  score: number;
  match_factors: {
    exact_amount_match?: boolean;
    amount_within_tolerance?: boolean;
    date_within_7_days?: boolean;
    tenant_name_in_description?: boolean;
    sci_id_match?: boolean;
  };
}

interface TransactionDetailSheetProps {
  transaction: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransactionDetailSheet({
  transaction,
  open,
  onOpenChange,
}: TransactionDetailSheetProps) {
  const queryClient = useQueryClient();
  const [suggestedMatches, setSuggestedMatches] = useState<SuggestedMatch[]>([]);
  const [topMatchReconciliationId, setTopMatchReconciliationId] = useState<string | null>(null);
  const [showManualDialog, setShowManualDialog] = useState(false);
  const [selectedRentPeriodId, setSelectedRentPeriodId] = useState<string>('');
  const [notes, setNotes] = useState('');

  // Fetch unpaid rent periods for manual matching
  const { data: rentPeriods = [] } = useQuery({
    queryKey: ['unpaid-rent-periods'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('rent_periods')
        .select(`
          id,
          period_yyyymm,
          rent_due,
          charges_due,
          total_due,
          total_paid,
          status,
          leases!inner (
            id,
            tenants!inner (first_name, last_name, company_name, tenant_type),
            properties!inner (address)
          )
        `)
        .in('status', ['pending', 'partial'])
        .order('period_yyyymm', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: showManualDialog,
  });

  // Suggest matches mutation
  const suggestMutation = useMutation({
    mutationFn: async (transactionId: string) => {
      const { data, error } = await supabase.functions.invoke('reconcile-transaction-suggest', {
        body: { transaction_id: transactionId },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setSuggestedMatches(data.matches || []);
      if (data.top_match_created && data.matches?.[0]?.reconciliation_id) {
        setTopMatchReconciliationId(data.matches[0].reconciliation_id);
      }
      if (data.matches?.length > 0) {
        toast.success(`${data.matches.length} correspondance(s) trouvée(s)`);
      } else {
        toast.info('Aucune correspondance automatique trouvée');
      }
    },
    onError: (error: Error) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });

  // Confirm match mutation
  const confirmMutation = useMutation({
    mutationFn: async ({ reconciliationId, notes }: { reconciliationId: string; notes?: string }) => {
      const { data, error } = await supabase.functions.invoke('reconcile-transaction-confirm', {
        body: { reconciliation_id: reconciliationId, notes },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Transaction rapprochée avec succès');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      setSuggestedMatches([]);
      setTopMatchReconciliationId(null);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });

  // Manual match mutation
  const manualMutation = useMutation({
    mutationFn: async ({ transactionId, rentPeriodId, notes }: { transactionId: string; rentPeriodId: string; notes?: string }) => {
      const { data, error } = await supabase.functions.invoke('reconcile-transaction-manual', {
        body: { 
          transaction_id: transactionId, 
          rent_period_id: rentPeriodId,
          notes 
        },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Transaction rapprochée manuellement');
      queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
      setShowManualDialog(false);
      setSelectedRentPeriodId('');
      setNotes('');
      onOpenChange(false);
    },
    onError: (error: Error) => {
      toast.error(`Erreur: ${error.message}`);
    },
  });

  if (!transaction) return null;

  const isReconcilable = transaction.reconciliation_status !== 'matched' && transaction.amount > 0;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'matched':
        return 'default';
      case 'pending_review':
        return 'secondary';
      case 'unreconciled':
        return 'outline';
      case 'excluded':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'matched':
        return 'Rapprochée';
      case 'pending_review':
        return 'En révision';
      case 'unreconciled':
        return 'Non rapprochée';
      case 'excluded':
        return 'Exclue';
      default:
        return status;
    }
  };

  const formatPeriod = (yyyymm: string) => {
    const [year, month] = yyyymm.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return format(date, 'MMMM yyyy', { locale: fr });
  };

  const getTenantName = (lease: any) => {
    const tenant = lease?.tenants;
    if (!tenant) return '-';
    if (tenant.tenant_type === 'company' && tenant.company_name) {
      return tenant.company_name;
    }
    return [tenant.first_name, tenant.last_name].filter(Boolean).join(' ') || '-';
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5" />
              Détails de la transaction
            </SheetTitle>
          </SheetHeader>

          <div className="space-y-4 mt-6">
            {/* Reconciliation Actions */}
            {isReconcilable && (
              <Card className="border-primary">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-primary">
                    <CreditCard className="h-4 w-4" />
                    Actions de rapprochement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex gap-2">
                    <Button
                      onClick={() => suggestMutation.mutate(transaction.id)}
                      disabled={suggestMutation.isPending}
                      className="flex-1"
                    >
                      {suggestMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      Rechercher correspondances
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowManualDialog(true)}
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Manuel
                    </Button>
                  </div>

                  {/* Suggested Matches */}
                  {suggestedMatches.length > 0 && (
                    <div className="space-y-2 pt-2">
                      <p className="text-sm font-medium">Correspondances suggérées:</p>
                      {suggestedMatches.map((match, idx) => (
                        <Card key={match.rent_period_id} className={idx === 0 ? 'border-green-500' : ''}>
                          <CardContent className="p-3">
                            <div className="flex justify-between items-start">
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <User className="h-3 w-3" />
                                  <span className="font-medium">{match.tenant_name}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Home className="h-3 w-3" />
                                  <span>{match.property_address}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                  <Calendar className="h-3 w-3" />
                                  <span>{formatPeriod(match.period_yyyymm)}</span>
                                  <span className="text-muted-foreground">•</span>
                                  <Euro className="h-3 w-3" />
                                  <span>{match.amount_due.toFixed(2)} €</span>
                                </div>
                                <div className="flex items-center gap-1 mt-1">
                                  <Percent className="h-3 w-3 text-primary" />
                                  <Badge variant={match.score >= 80 ? 'default' : match.score >= 60 ? 'secondary' : 'outline'}>
                                    Score: {match.score}%
                                  </Badge>
                                  {match.match_factors.exact_amount_match && (
                                    <Badge variant="outline" className="text-xs">Montant exact</Badge>
                                  )}
                                  {match.match_factors.tenant_name_in_description && (
                                    <Badge variant="outline" className="text-xs">Nom trouvé</Badge>
                                  )}
                                </div>
                              </div>
                              {idx === 0 && topMatchReconciliationId && (
                                <Button
                                  size="sm"
                                  onClick={() => confirmMutation.mutate({ 
                                    reconciliationId: topMatchReconciliationId 
                                  })}
                                  disabled={confirmMutation.isPending}
                                >
                                  {confirmMutation.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4 mr-1" />
                                  )}
                                  Confirmer
                                </Button>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* SCI and Bank Account */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  SCI et Compte
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">SCI</p>
                  <p className="font-bold text-lg">{transaction.bank_accounts?.scis?.name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Compte bancaire</p>
                  <p className="font-medium">
                    {transaction.bank_accounts?.account_name || '-'}
                    {transaction.bank_accounts?.iban &&
                      ` (...${transaction.bank_accounts.iban.slice(-4)})`
                    }
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Transaction Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Détails de la transaction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Date</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {format(new Date(transaction.transaction_date), 'dd MMMM yyyy', { locale: fr })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Montant</p>
                  <p className={`font-bold text-2xl flex items-center gap-1 ${
                    transaction.amount > 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    <Euro className="h-5 w-5" />
                    {transaction.amount > 0 ? '+' : ''}{transaction.amount.toFixed(2)} €
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="font-medium">{transaction.description || '-'}</p>
                </div>
                {transaction.category && (
                  <div>
                    <p className="text-sm text-muted-foreground">Catégorie</p>
                    <Badge variant="outline">{transaction.category}</Badge>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Counterparty Information */}
            {(transaction.counterparty_name || transaction.counterparty_iban) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Contrepartie
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {transaction.counterparty_name && (
                    <div>
                      <p className="text-sm text-muted-foreground">Nom</p>
                      <p className="font-medium">{transaction.counterparty_name}</p>
                    </div>
                  )}
                  {transaction.counterparty_iban && (
                    <div>
                      <p className="text-sm text-muted-foreground">IBAN</p>
                      <p className="font-mono text-sm">{transaction.counterparty_iban}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Reconciliation Status */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Rapprochement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="text-sm text-muted-foreground">Statut</p>
                  <Badge variant={getStatusColor(transaction.reconciliation_status) as any}>
                    {getStatusLabel(transaction.reconciliation_status)}
                  </Badge>
                </div>
                {transaction.reconciliation_confidence && (
                  <div>
                    <p className="text-sm text-muted-foreground">Confiance</p>
                    <p className="font-medium">{transaction.reconciliation_confidence}%</p>
                  </div>
                )}
                {transaction.reconciled_at && (
                  <div>
                    <p className="text-sm text-muted-foreground">Rapprochée le</p>
                    <p className="font-medium">
                      {format(new Date(transaction.reconciled_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Additional Info */}
            {transaction.reference && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Référence bancaire</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{transaction.reference}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Manual Matching Dialog */}
      <Dialog open={showManualDialog} onOpenChange={setShowManualDialog}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5" />
              Rapprochement manuel
            </DialogTitle>
            <DialogDescription>
              Sélectionnez la période de loyer à associer à cette transaction de {transaction?.amount?.toFixed(2)} €
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Période de loyer</Label>
              <Select value={selectedRentPeriodId} onValueChange={setSelectedRentPeriodId}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionnez une période..." />
                </SelectTrigger>
                <SelectContent>
                  {rentPeriods.map((period: any) => (
                    <SelectItem key={period.id} value={period.id}>
                      <div className="flex flex-col">
                        <span>
                          {getTenantName(period.leases)} - {formatPeriod(period.period_yyyymm)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {period.leases?.properties?.address} • Dû: {period.total_due?.toFixed(2)} € • Payé: {period.total_paid?.toFixed(2)} €
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ajouter des notes sur ce rapprochement..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={() => {
                if (transaction?.id && selectedRentPeriodId) {
                  manualMutation.mutate({
                    transactionId: transaction.id,
                    rentPeriodId: selectedRentPeriodId,
                    notes: notes || undefined,
                  });
                }
              }}
              disabled={!selectedRentPeriodId || manualMutation.isPending}
            >
              {manualMutation.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Confirmer le rapprochement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
